/* eslint-disable complexity */
import { ObjectLiteral } from "../../../constants";
import {
    eGroupFilter,
    egroupFilterValue,
    eGroupHome,
    processedEGroupFilterContent,
} from "../../../store/egroups/types";
import { Campus } from "../../../store/main/types";
import _ from "lodash";
import queryString from "query-string";
import { eGroupFiltersValues } from "../home/Filters";
import { ParsedUrlQuery } from "querystring";

export class Helpers {
    //standarize the filter names everwhere they are being referenced
    readonly campusFilterName = "campus";
    readonly inPersonFilterName = "inPerson";
    readonly demographicFilterName = "demographic";
    readonly daysFilterName = "days";
    readonly topicFilterName = "topic";
    readonly eGroupNameFilterName = "egroup_name";
    readonly eGroupRegionFilterName = "egroup_region";
    readonly languageFilterName = "language";
    readonly childCareFilterName = "childCare";

    getExpandedFilters = (
        eGroupHomeData: eGroupHome | undefined,
        queryString: ParsedUrlQuery
    ) => {
        const expandedFilters: string[] = [];
        eGroupHomeData?.templateFilters?.forEach((filter) => {
            if (
                filter.name !== this.campusFilterName &&
                queryString[filter.name]
            ) {
                expandedFilters.push(filter.name);
            }
        });

        return expandedFilters;
    };

    static getQueryParamsWithoutCampus = (queryString: ParsedUrlQuery) => {
        const parsedQuerystring = _.cloneDeep(queryString);
        if (parsedQuerystring?.campus) {
            delete parsedQuerystring.campus;
        }
        return parsedQuerystring;
    };

    webpSupported = () => {
        if (typeof window !== "undefined") {
            let elem = document.createElement("canvas");
            if (!!(elem.getContext && elem.getContext("2d"))) {
                return (
                    elem.toDataURL("image/webp").indexOf("data:image/webp") ===
                    0
                );
            } else {
                return false;
            }
        }
    };

    static toTitleCase = (str: string, alteredValue: boolean) => {
        function applySubstitutions(str: string): string {
            const list: ObjectLiteral = {
                yes: "Childcare",
                "winston-salem": "Winston-Salem",
                "in-person": "In-person",
                "in-person & virtual": "In-person & Virtual",
                "youth--youth": "Youth: Seasonal",
            };
            return list[str] ?? str;
        }

        if (alteredValue) {
            return str;
        }

        if (str !== applySubstitutions(str)) {
            return applySubstitutions(str);
        }

        if (_.toLower(str).indexOf("egroup") === 0) {
            return _.replace(str, "-", ": ").replace("egroup", "eGroup");
        } else {
            let startCaseWords = _.toLower(str).replace(
                /([A-Za-z'])([A-Za-z']*)/g,
                function (_, i, r) {
                    return i.toUpperCase() + (r != null ? r : "");
                }
            );

            if (_.toLower(str).indexOf("yth") > -1) {
                startCaseWords = startCaseWords.replace("yth", "YTH");
                startCaseWords = startCaseWords.replace("Yth", "YTH");
            }

            if (_.toLower(str).indexOf("egroup") > -1) {
                startCaseWords = startCaseWords.replace("egroup", "eGroup");
            }

            startCaseWords = startCaseWords.replace(" - ", ": ");
            if (startCaseWords.includes("--")) {
                let separatedString = startCaseWords.split("-");
                let firstString = separatedString[0];
                let secondString = separatedString[2].split(":")[0];
                let afterColonString = separatedString[2].split(":")[1];
                if (firstString === secondString) {
                    startCaseWords = `${firstString}:${afterColonString}`;
                } else {
                    startCaseWords = `${firstString}: ${secondString}`;
                }
            }
            return startCaseWords.replace(/-/g, " ");
        }
    };

    getAPIReadyQuery = (
        eGroupHomeData: eGroupHome | undefined,
        queryString: ParsedUrlQuery,
        addBlockedCampuses: boolean,
        loadFilters: boolean,
        campuses: Campus[],
        paginationQuery?: ObjectLiteral,
        blockedCampus: string[] = [],
        eGroupGlobalQuery?: ObjectLiteral,
        currentFilterBeingApplied?: string[]
    ): string => {
        let queryToSend: string[] = [];
        let filters: ObjectLiteral = {
            where: {
                and: [],
            },
        };

        let currentFilterBeingAppliedToSend: {
            filterConditions: ObjectLiteral;
            filterName: string;
        }[] = [];

        eGroupHomeData?.templateFilters?.forEach((filter) => {
            if (filter.name !== this.campusFilterName) {
                const currentFilterBeingAppliedConditions: ObjectLiteral = {
                    and: [],
                };

                if (
                    queryString[filter.name] &&
                    _.isArray(queryString[filter.name])
                ) {
                    const filterOptions: ObjectLiteral[] = [];

                    (queryString[filter.name] as string[]).forEach(
                        (value: string) => {
                            if (value.indexOf("--") > -1) {
                                const filterParts = value.split("--");
                                if (
                                    filter.fields.length === filterParts.length
                                ) {
                                    const filterAndParts: ObjectLiteral[] = [];
                                    filter.fields
                                        .slice()
                                        .reverse()
                                        .forEach((filterField, index) => {
                                            const filterAndObj: ObjectLiteral =
                                                {};

                                            filterAndObj[filterField] =
                                                encodeURIComponent(
                                                    filterParts[index]
                                                );

                                            filterAndParts.push(filterAndObj);
                                        });
                                    filterOptions.push({ and: filterAndParts });
                                }
                            } else if (filter.fields.length === 1) {
                                const filterAndObj: ObjectLiteral = {};
                                filterAndObj[filter.fields[0]] =
                                    encodeURIComponent(value as string);

                                filterOptions.push(filterAndObj);
                            }
                        }
                    );

                    if (filterOptions && !_.isEmpty(filterOptions)) {
                        if (
                            currentFilterBeingApplied &&
                            currentFilterBeingApplied.indexOf(filter.name) > -1
                        ) {
                            currentFilterBeingAppliedConditions.and.push({
                                or: filterOptions,
                            });
                        } else {
                            filters.where.and.push({ or: filterOptions });
                        }
                    }
                } else if (queryString[filter.name]) {
                    const filterValue = queryString[filter.name];

                    if (
                        (filterValue && filterValue.indexOf("--") > -1) ||
                        filter.fields.length > 1
                    ) {
                        const demographicParts = (filterValue as string).split(
                            "--"
                        );

                        // if (filter.fields.length === demographicParts.length) {
                        const filterAndParts: ObjectLiteral[] = [];
                        filter.fields
                            .slice()
                            .reverse()
                            .forEach((filterField, index) => {
                                const filterAndObj: ObjectLiteral = {};
                                filterAndObj[filterField] = encodeURIComponent(
                                    demographicParts[index] as string
                                );

                                if (demographicParts[index])
                                    filterAndParts.push(filterAndObj);
                            });

                        if (
                            currentFilterBeingApplied &&
                            currentFilterBeingApplied.indexOf(filter.name) > -1
                        ) {
                            currentFilterBeingAppliedConditions.and.push({
                                and: filterAndParts,
                            });
                        } else {
                            filters.where.and.push({ and: filterAndParts });
                        }
                        // }
                    } else if (filter.fields.length === 1) {
                        const filterAndObj: ObjectLiteral = {};
                        let value;

                        if (filter.type === "boolean") {
                            value = queryString[filter.name] === "yes";
                        } else {
                            value = encodeURIComponent(
                                queryString[filter.name] as string
                            );
                        }

                        filterAndObj[filter.fields[0]] = value;

                        if (
                            currentFilterBeingApplied &&
                            currentFilterBeingApplied.indexOf(filter.name) > -1
                        ) {
                            currentFilterBeingAppliedConditions.and.push(
                                filterAndObj
                            );
                        } else {
                            filters.where.and.push(filterAndObj);
                        }
                    }
                }

                if (currentFilterBeingAppliedConditions.and.length > 1) {
                    currentFilterBeingAppliedToSend.push({
                        filterConditions: currentFilterBeingAppliedConditions,
                        filterName: filter.name,
                    });
                } else if (
                    currentFilterBeingAppliedConditions.and.length == 1
                ) {
                    currentFilterBeingAppliedToSend.push({
                        filterConditions:
                            currentFilterBeingAppliedConditions.and[0],
                        filterName: filter.name,
                    });
                }
            }
        });

        let campusCondition: ObjectLiteral[] = [];

        if (addBlockedCampuses && blockedCampus && !_.isEmpty(blockedCampus)) {
            blockedCampus.forEach((campus: string) => {
                const campusValue = this.getQueryCampusValue(campus, campuses);
                if (campusValue) {
                    campusCondition.push({ campusId: campusValue.id });
                }
            });
        }

        if (queryString.campus && _.isArray(queryString.campus)) {
            queryString.campus.forEach((campus: string) => {
                const campusValue = this.getQueryCampusValue(campus, campuses);
                if (campusValue) {
                    campusCondition.push({ campusId: campusValue.id });
                }
            });
        } else if (queryString.campus) {
            const campusValue = this.getQueryCampusValue(
                queryString.campus as string,
                campuses
            );

            if (campusValue) {
                campusCondition.push({ campusId: campusValue.id });
            }
        }

        if (queryString[this.eGroupNameFilterName]) {
            filters.where.and.push({
                name: {
                    like: `.*${queryString[this.eGroupNameFilterName]}.*`,
                    options: "i",
                },
            });
        }

        if (queryString[this.eGroupRegionFilterName]) {
            filters.where.and.push({
                region: queryString[this.eGroupRegionFilterName],
            });
        }

        if (queryString.locationQuery) {
            filters.where.and.push({ location: queryString.locationQuery });
        }

        if (filters.where.and.length === 0) {
            filters = {};
        }

        if (queryString.order_by && queryString.order_by_direction) {
            filters.order = [
                `${queryString.order_by} ${queryString.order_by_direction}`,
            ];
        } else {
            filters.order = ["dayOfWeekNumber asc", "name asc"];
        }

        if (loadFilters && eGroupHomeData && eGroupHomeData.templateFilters) {
            queryToSend.push(
                `pageMeta=${JSON.stringify({
                    filters: eGroupHomeData.templateFilters,
                })}`
            );
            queryToSend.push(`returnFilters=true`);
        }

        if (campusCondition && campusCondition.length > 0) {
            queryToSend.push(
                `campusCondition=${JSON.stringify({
                    campus: campusCondition,
                    campusFilterName: this.campusFilterName,
                })}`
            );
        }

        if (
            currentFilterBeingAppliedToSend &&
            currentFilterBeingAppliedToSend.length
        ) {
            queryToSend.push(
                `currentFilter=${JSON.stringify({
                    filters: currentFilterBeingAppliedToSend,
                })}`
            );
        }

        if (eGroupGlobalQuery) {
            queryToSend.push(
                `requiredFilters=${JSON.stringify(eGroupGlobalQuery)}`
            );
        }

        if (paginationQuery) {
            filters = {
                ...filters,
                ...paginationQuery,
            };
        }

        queryToSend.push(`filter=${JSON.stringify(filters)}`);

        return queryToSend.join("&");
    };

    validateDay = (day: string): boolean => {
        switch (day.toLocaleLowerCase()) {
            case "sunday":
            case "monday":
            case "tuesday":
            case "wednesday":
            case "thursday":
            case "friday":
            case "saturday":
            case "today":
                return true;
            default:
                return false;
        }
    };

    getQueryCampusValue = (
        campusToSearch: string,
        campuses: Campus[]
    ): Campus | undefined => {
        let campusToReturn;

        campuses.forEach((campus) => {
            if (campus.slugName === campusToSearch) {
                campusToReturn = campus;
            }
        });

        return campusToReturn;
    };

    mapFilterValues = (
        filtersParam: eGroupFilter[],
        eGroupHomeData: eGroupHome | undefined
    ): eGroupFiltersValues | null => {
        if (
            !eGroupHomeData ||
            !eGroupHomeData.templateFilters ||
            eGroupHomeData.templateFilters.length < 1
        ) {
            return null;
        }

        const filterStructure: processedEGroupFilterContent[] = [];

        const filtersMetaOrdered = _.orderBy(
            eGroupHomeData?.templateFilters,
            "order"
        );

        const constreverseArrayInPlace = (arr: any[]) => {
            let newArray: any[] = [];
            for (let i = 0; i <= arr.length / 2; i++) {
                let el = arr[i];
                newArray[i] = arr[arr.length - 1 - i];
                newArray[arr.length - 1 - i] = el;
            }
            return newArray;
        };

        const filters = _.cloneDeep(filtersParam);

        filtersMetaOrdered.forEach((filter) => {
            const filterValuesPos = _.findIndex(filters, { name: filter.name });

            if (filterValuesPos === -1) {
                return null;
            }

            const filterData: eGroupFilter = Object.assign(
                {},
                filters[filterValuesPos]
            );
            const filterSlugName = filterData.label?.replaceAll(" ", "-");
            const realFieldOrderControl = filterData.fields.slice(0);

            const fieldValueParser = (
                fieldDataToCheck: ObjectLiteral,
                fieldIndexToCheck: number
            ): egroupFilterValue[] => {
                const currentField = realFieldOrderControl[fieldIndexToCheck];
                if (currentField) {
                    if (fieldDataToCheck[currentField]) {
                        let dataToReturn: egroupFilterValue[] =
                            fieldDataToCheck[currentField].map(
                                (value: egroupFilterValue) => {
                                    const valueLabelPos = _.findIndex(
                                        filter.valuesLabel,
                                        { value: value.value }
                                    );

                                    let label = value.value;

                                    if (
                                        valueLabelPos > -1 &&
                                        filter.valuesLabel
                                    ) {
                                        label =
                                            filter.valuesLabel[valueLabelPos]
                                                .label;
                                    }

                                    return {
                                        value: value.value,
                                        label, // label needed for future manipulation of the data
                                        count: value.count,
                                        children: fieldValueParser(
                                            value,
                                            fieldIndexToCheck - 1
                                        ),
                                    };
                                }
                            );
                        // Order by label
                        dataToReturn.sort(
                            (a: egroupFilterValue, b: egroupFilterValue) => {
                                return a.label
                                    .toString()
                                    .localeCompare(b.label);
                            }
                        );
                        return dataToReturn;
                    }
                }

                return [];
            };

            let values = fieldValueParser(
                filterData,
                realFieldOrderControl.length - 1
            );

            filterStructure.push({
                ...filterData,
                realFieldOrder: constreverseArrayInPlace(filterData.fields),
                values: this.eGroupFilterValuesSorter(values, filterData),
                slugName: filterSlugName,
            });
        });

        return {
            filters: filterStructure,
            timeProcessed: Date.now(),
        };
    };

    eGroupFilterValuesSorter = (
        values: egroupFilterValue[],
        filterData: eGroupFilter
    ) => {
        return values.sort((a: egroupFilterValue, b: egroupFilterValue) => {
            if (
                filterData.orderValuesBy &&
                (a[
                    filterData.orderValuesBy as keyof egroupFilterValue
                ] as keyof egroupFilterValue)
            ) {
                if (filterData.sorter) {
                    if (filterData.orderValuesByDirection === "desc") {
                        return (
                            filterData.sorter[
                                b[
                                    filterData.orderValuesBy as keyof egroupFilterValue
                                ] as keyof egroupFilterValue
                            ] -
                            filterData.sorter[
                                a[
                                    filterData.orderValuesBy as keyof egroupFilterValue
                                ] as keyof egroupFilterValue
                            ]
                        );
                    }

                    return (
                        filterData.sorter[
                            a[
                                filterData.orderValuesBy as keyof egroupFilterValue
                            ] as keyof egroupFilterValue
                        ] -
                        filterData.sorter[
                            b[
                                filterData.orderValuesBy as keyof egroupFilterValue
                            ] as keyof egroupFilterValue
                        ]
                    );
                } else {
                    let order = 0;

                    if (
                        a[filterData.orderValuesBy as keyof egroupFilterValue] <
                        b[filterData.orderValuesBy as keyof egroupFilterValue]
                    ) {
                        order =
                            filterData.orderValuesByDirection === "desc"
                                ? 1
                                : -1;
                    }
                    if (
                        a[filterData.orderValuesBy as keyof egroupFilterValue] >
                        b[filterData.orderValuesBy as keyof egroupFilterValue]
                    ) {
                        order =
                            filterData.orderValuesByDirection === "desc"
                                ? -1
                                : 1;
                    }

                    return order;
                }
            } else if (filterData.orderValuesBy) {
                return 0;
            }

            return b.children.length - a.children.length;
        });
    };
}
